import { OrderStatus } from "@app/types/orders";
import { OrderKind, StopStatus } from "@app/domain";

/**
 * There is no dedicated "is the pickup done" field on the real API's
 * `OrderPickupAddress` — pickup progress has to be read off the order's own
 * `status`/`stage`. Dropoffs are different: `OrderDropoffAddress`/
 * `OrderDeliveryAddress` each carry their own `status` string
 * independently of the order's overall status (a multi-dropoff order can
 * have one dropoff done, one in transit, one returned, all at once) — see
 * `deriveDropoffStatus` below, which reads that per-stop field instead.
 */
export function derivePickupStatus(kind: OrderKind, orderStatus: OrderStatus, stage: "Pickup" | "Delivery" | undefined): StopStatus {
  if (kind === "pickupDelivery") {
    if (stage === "Delivery") return "done"; // pickup phase already completed
    switch (orderStatus) {
      case "PickedUp":
      case "AtPickupServicePoint":
        return "done";
      case "PickupFailed":
      case "Lost":
        return "failed";
      case "Returned":
        return "returned";
      case "CustomerCanceled":
      case "SupportCanceled":
        return "failed";
      default:
        return "pending"; // Draft/Confirmed/PickupRouted/ReadyForPickup
    }
  }

  // Ondemand / Pickup / Delivery
  switch (orderStatus) {
    case "PickedUp":
    case "Done":
      return "done";
    case "Started":
    case "Arrived":
      return "inProgress";
    case "CustomerCanceled":
    case "DriverCanceled":
    case "SupportCanceled":
      return "failed";
    default:
      return "pending"; // Pending/Unassigned/Assigned
  }
}

/** `rawStatus` is the per-stop `status` field carried on the Redux
 * `Address` shape (`Address.status`, populated straight from
 * `OrderDropoffAddress.status`/`OrderDeliveryAddress.status`). */
export function deriveDropoffStatus(kind: OrderKind, rawStatus: string | undefined): StopStatus {
  if (kind === "pickupDelivery") {
    switch (rawStatus) {
      case "Delivered":
        return "done";
      case "NotDelivered":
      case "PickupFailed":
      case "Lost":
      case "CustomerCanceled":
      case "SupportCanceled":
        return "failed";
      case "Returned":
      case "ForReturn":
        return "returned";
      default:
        return "inProgress"; // AtWarehouse/ReadyForTransit/InTransit/DeliveryRouted/ReadyForDelivery/OutForDelivery/AtDeliveryServicePoint
    }
  }

  // Ondemand / Pickup / Delivery: OrderDropoffAddress.status is just Pending|Done
  return rawStatus === "Done" ? "done" : "pending";
}

/** Short, human status label per stop — production has no dedicated
 * per-stop status text (only the order-level `statusDescription`), so this
 * is synthesized presentation text, not a business rule. */
export function stopStatusLabel(kind: "pickup" | "dropoff", status: StopStatus): string {
  switch (status) {
    case "done":
      return kind === "pickup" ? "Picked up" : "Delivered";
    case "inProgress":
      return kind === "pickup" ? "Picking up" : "Out for delivery";
    case "failed":
      return "Failed";
    case "returned":
      return "Returned";
    default:
      return "Pending";
  }
}
